import { createAsyncThunk, createSlice } from '@reduxjs/toolkit';
import type {
    AdminCenterDataRequest,
    AdminCenterDataResponse,
} from '@routes/sidebar/___shared___/api/adminCenterData.api.ts';
import { getApiAdminCenterData } from '@routes/sidebar/___shared___/api/adminCenterData.api.ts';
import { EMPTY_ADMIN_CENTER_DATA } from '@routes/sidebar/___shared___/api/constants.ts';
import { extractError } from '@utils/slice.ts';

export interface AdminCenterDataError {
    message?: string;
    [k: string]: any;
}

interface AdminCenterDataSlice {
    error: AdminCenterDataError | null;
    commonAdminCenterData: AdminCenterDataResponse;
    isGetAdminCenterDataPending: boolean;
}

const initialState: AdminCenterDataSlice = {
    error: null,
    commonAdminCenterData: EMPTY_ADMIN_CENTER_DATA,
    isGetAdminCenterDataPending: false,
};

export const getAdminCenterData = createAsyncThunk<
    AdminCenterDataResponse,
    { tenantAlias: string; payload: AdminCenterDataRequest },
    { rejectValue: AdminCenterDataError }
>('adminCenterData/getAdminCenterData', ({ tenantAlias, payload }, { rejectWithValue }) =>
    getApiAdminCenterData(tenantAlias, payload)
        .then((res) => res)
        .catch((err) => rejectWithValue(err)),
);

const adminCenterDataSlice = createSlice({
    name: 'adminCenterData',
    initialState,
    reducers: {
        resetAdminCenterData: (state) => {
            state.error = null;
            state.commonAdminCenterData = EMPTY_ADMIN_CENTER_DATA;
            state.isGetAdminCenterDataPending = false;
        },
    },
    extraReducers: (builder) => {
        builder.addCase(getAdminCenterData.pending, (state) => {
            state.error = null;
            state.isGetAdminCenterDataPending = true;
        });

        builder.addCase(getAdminCenterData.fulfilled, (state, action) => {
            state.commonAdminCenterData = action.payload;
            state.isGetAdminCenterDataPending = false;
        });

        builder.addCase(getAdminCenterData.rejected, (state, action) => {
            state.error = extractError(action);
            state.isGetAdminCenterDataPending = false;
        });
    },
});

export const { resetAdminCenterData } = adminCenterDataSlice.actions;

export default adminCenterDataSlice.reducer;
